import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

import VideoPreview from '@/components/media/VideoPreview.js';
import { fetchJianyingDevices } from '@/features/systemManagement/jianying/services/jianyingDeviceService.js';
import { fetchJianyingTemplateAvailability } from '@/features/systemManagement/jianying/services/jianyingTemplateService.js';
import { getJianyingDeviceSchedulingState } from '@/features/systemManagement/jianying/utils/jianyingDevices.js';
import {
  downloadJianyingJob,
  fetchJianyingJobAttempts,
  fetchJianyingJobs,
  retryJianyingJob,
} from '../../services/jianyingJobService.js';
import {
  JIANYING_JOB_STATUS,
  getJianyingJobStatusLabel,
  getJianyingStatusTone,
} from '../../constants/jianyingJobStatus.js';
import {
  getJianyingAttemptStageLabel,
  shouldDefaultExpandJianyingAttemptCycle,
} from '../../utils/jianyingJobAttempts.js';
import { createJianyingRequestGuard } from '../../utils/jianyingSlots.js';
import '../../../../jianying/JianyingCommon.scss';
import '../../JianyingAutomation.scss';

const PAGE_SIZE = 15;

const formatTime = (value) => value
  ? new Date(value).toLocaleString('zh-CN', { hour12: false })
  : '-';

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

const ATTEMPT_STATUS_LABELS = Object.freeze({
  Assigned: '已分配',
  Running: '执行中',
  Success: '成功',
  Failed: '失败',
  LeaseExpired: '租约过期',
  Superseded: '已被替换',
});

const getAttemptStatusTone = (status) => {
  if (status === 'Success') return 'success';
  if (status === 'Assigned' || status === 'Superseded') return 'muted';
  if (status === 'Running') return 'progress';
  return 'danger';
};

const JianyingJobsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightedJobId = searchParams.get('job_id') || '';
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState('');
  const [searchText, setSearchText] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState(highlightedJobId);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [message, setMessage] = useState('');
  const [devices, setDevices] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attemptMessage, setAttemptMessage] = useState('');
  const [attemptRefreshKey, setAttemptRefreshKey] = useState(0);
  const [retryCandidate, setRetryCandidate] = useState(null);
  const [retryPreferredDeviceId, setRetryPreferredDeviceId] = useState('');
  const [retryEligibleDeviceIds, setRetryEligibleDeviceIds] = useState([]);
  const [retryDevicesLoading, setRetryDevicesLoading] = useState(false);
  const [retryDeviceMessage, setRetryDeviceMessage] = useState('');
  const requestGuard = useRef(null);
  if (!requestGuard.current) {
    requestGuard.current = createJianyingRequestGuard();
  }

  const loadJobs = useCallback(async ({ silent = false } = {}) => {
    const requestId = requestGuard.current.begin();
    if (!silent) {
      setLoading(true);
      setMessage('');
    }
    try {
      const [result, deviceData] = await Promise.all([
        fetchJianyingJobs({
          page,
          limit: PAGE_SIZE,
          status: status || undefined,
          keyword: keyword || undefined,
        }),
        fetchJianyingDevices().catch(() => null),
      ]);
      if (!requestGuard.current.isCurrent(requestId)) return;
      setJobs(result.items);
      setTotal(result.total);
      if (deviceData) setDevices(deviceData);
      setSelectedJobId((current) => {
        if (current && result.items.some((job) => job.id === current)) return current;
        if (highlightedJobId && result.items.some((job) => job.id === highlightedJobId)) return highlightedJobId;
        return result.items[0]?.id || '';
      });
    } catch (error) {
      if (!silent && requestGuard.current.isCurrent(requestId)) {
        setMessage(getErrorMessage(error, '读取剪映任务失败'));
      }
    } finally {
      if (requestGuard.current.isCurrent(requestId)) setLoading(false);
    }
  }, [highlightedJobId, keyword, page, status]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setKeyword(searchText.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    loadJobs();
    return () => requestGuard.current.invalidate();
  }, [loadJobs]);

  useEffect(() => {
    const timer = window.setInterval(() => loadJobs({ silent: true }), 10000);
    return () => window.clearInterval(timer);
  }, [loadJobs]);

  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === selectedJobId) || null,
    [jobs, selectedJobId],
  );
  const deviceNameMap = useMemo(
    () => new Map(devices.map((device) => [String(device.id), device.name])),
    [devices],
  );
  const retryEligibleDevices = useMemo(() => {
    const eligibleIds = new Set(retryEligibleDeviceIds.map(String));
    return devices.filter(
      (device) =>
        getJianyingDeviceSchedulingState(device).schedulable
        && eligibleIds.has(String(device.id)),
    );
  }, [devices, retryEligibleDeviceIds]);
  const attemptsByCycle = useMemo(() => {
    const grouped = new Map();
    attempts.forEach((attempt) => {
      const cycle = Number(attempt.dispatch_cycle_no) || 1;
      if (!grouped.has(cycle)) grouped.set(cycle, []);
      grouped.get(cycle).push(attempt);
    });
    return Array.from(grouped.entries())
      .sort(([left], [right]) => right - left)
      .map(([cycle, items]) => ({ cycle, items }));
  }, [attempts]);
  const displayedJobError = useMemo(() => {
    if (selectedJob?.error_code !== 'no_available_device') {
      return selectedJob;
    }
    return attempts.find((attempt) => attempt.error_code) || selectedJob;
  }, [attempts, selectedJob]);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (!selectedJobId) {
      setAttempts([]);
      setAttemptMessage('');
      return undefined;
    }
    let active = true;
    setAttemptsLoading(true);
    setAttemptMessage('');
    fetchJianyingJobAttempts(selectedJobId)
      .then((items) => {
        if (active) setAttempts(items);
      })
      .catch((error) => {
        if (active) {
          setAttempts([]);
          setAttemptMessage(getErrorMessage(error, '读取设备执行记录失败'));
        }
      })
      .finally(() => {
        if (active) setAttemptsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attemptRefreshKey, selectedJobId]);

  const openRetryDialog = async (job) => {
    setRetryCandidate(job);
    setRetryPreferredDeviceId('');
    setRetryEligibleDeviceIds([]);
    setRetryDeviceMessage('');
    setRetryDevicesLoading(true);
    try {
      const items = await fetchJianyingTemplateAvailability([
        job.jianying_template_id,
      ]);
      const availability = items.find(
        (item) => String(item.template_id) === String(job.jianying_template_id),
      );
      setRetryEligibleDeviceIds(availability?.available_device_ids || []);
    } catch (error) {
      setRetryDeviceMessage(getErrorMessage(
        error,
        '暂时无法读取模板设备状态，仍可使用自动选择。',
      ));
    } finally {
      setRetryDevicesLoading(false);
    }
  };

  const retryJob = async () => {
    if (!retryCandidate) return;
    setBusyId(retryCandidate.id);
    setMessage('');
    try {
      await retryJianyingJob(
        retryCandidate.id,
        retryPreferredDeviceId || null,
      );
      setRetryCandidate(null);
      await loadJobs();
      setAttemptRefreshKey((current) => current + 1);
    } catch (error) {
      setMessage(getErrorMessage(error, '重试剪映任务失败'));
    } finally {
      setBusyId('');
    }
  };

  const downloadJob = async (job) => {
    setBusyId(job.id);
    setMessage('');
    try {
      await downloadJianyingJob(job.id);
    } catch (error) {
      setMessage(getErrorMessage(error, '下载剪映视频失败'));
    } finally {
      setBusyId('');
    }
  };

  return (
    <main className="jianying-workspace jianying-jobs">
      <header className="jianying-jobs__header">
        <div>
          <h2>剪映视频任务</h2>
          <p>生成草稿可并发，剪映导出按顺序执行。</p>
        </div>
        <Button onClick={() => navigate('/automation/jianying/create')}>
          <i className="bi bi-plus-lg" /> 创建任务
        </Button>
      </header>

      <div className="jianying-toolbar mb-3">
        <Form.Control
          aria-label="订单号或客户名称"
          placeholder="搜索订单号或客户名称"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
        <Form.Select
          aria-label="任务状态"
          value={status}
          onChange={(event) => { setStatus(event.target.value); setPage(1); }}
        >
          <option value="">全部状态</option>
          {Object.values(JIANYING_JOB_STATUS).map((value) => (
            <option key={value} value={value}>{getJianyingJobStatusLabel(value)}</option>
          ))}
        </Form.Select>
        <Button variant="outline-secondary" onClick={() => loadJobs()} disabled={loading} title="刷新任务">
          {loading
            ? <Spinner animation="border" size="sm" aria-label="正在刷新" />
            : <i className="bi bi-arrow-clockwise" />}
          {' '}刷新
        </Button>
      </div>

      {message && <p className="jianying-notice mb-3" role="alert">{message}</p>}

      <div className="jianying-jobs__layout">
        <section>
          <div className="jianying-table-wrap">
            <Table hover responsive={false}>
              <thead className="table-light">
                <tr><th>客户</th><th>模板</th><th>草稿名称</th><th>状态</th><th>当前设备</th><th>创建时间</th><th className="text-end">操作</th></tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    className={job.id === selectedJobId ? 'table-active' : ''}
                    onClick={() => setSelectedJobId(job.id)}
                  >
                    <td>{job.client_name || job.client_id}</td>
                    <td>{job.template_name || job.jianying_template_id}</td>
                    <td className="jianying-jobs__draft-name">{job.generated_draft_name}</td>
                    <td><span className={`jianying-status jianying-status--${getJianyingStatusTone(job.status)}`}>{getJianyingJobStatusLabel(job.status)}</span></td>
                    <td>{deviceNameMap.get(String(job.assigned_device_id)) || job.assigned_device_id || '待分配'}</td>
                    <td>{formatTime(job.create_time)}</td>
                    <td>
                      <div className="d-flex justify-content-end gap-2">
                        {job.status === JIANYING_JOB_STATUS.SUCCESS && (
                          <Button size="sm" variant="outline-primary" disabled={Boolean(busyId)} onClick={(event) => { event.stopPropagation(); downloadJob(job); }} title="下载成片">
                            {busyId === job.id
                              ? <Spinner animation="border" size="sm" aria-label="正在下载" />
                              : <i className="bi bi-download" />}
                          </Button>
                        )}
                        {job.status === JIANYING_JOB_STATUS.FAILED && (
                          <Button size="sm" variant="outline-danger" disabled={Boolean(busyId)} onClick={(event) => { event.stopPropagation(); openRetryDialog(job); }}>
                            {busyId === job.id
                              ? <Spinner animation="border" size="sm" aria-label="正在重试" />
                              : <i className="bi bi-arrow-repeat" />}
                            {' '}重试
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && jobs.length === 0 && <tr><td colSpan="7" className="jianying-empty">暂无剪映任务</td></tr>}
                {loading && (
                  <tr>
                    <td colSpan="7" className="jianying-empty">
                      <Spinner animation="border" size="sm" /> 正在读取任务
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          <div className="jianying-pager mt-3">
            <span>共 {total} 条，第 {page}/{pageCount} 页</span>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><i className="bi bi-chevron-left" /> 上一页</Button>
              <Button size="sm" variant="outline-secondary" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>下一页 <i className="bi bi-chevron-right" /></Button>
            </div>
          </div>
        </section>

        <aside className="jianying-job-detail">
          <h3>任务详情</h3>
          {!selectedJob && <div className="jianying-empty">选择任务查看详情</div>}
          {selectedJob && (
            <>
              <dl>
                <div><dt>订单号</dt><dd>{selectedJob.client_id}</dd></div>
                <div><dt>客户</dt><dd>{selectedJob.client_name || '-'}</dd></div>
                <div><dt>生成草稿名</dt><dd>{selectedJob.generated_draft_name}</dd></div>
                <div><dt>状态</dt><dd>{getJianyingJobStatusLabel(selectedJob.status)}</dd></div>
                <div><dt>当前设备</dt><dd>{deviceNameMap.get(String(selectedJob.assigned_device_id)) || selectedJob.assigned_device_id || '待分配'}</dd></div>
                <div><dt>开始时间</dt><dd>{formatTime(selectedJob.started_at)}</dd></div>
                <div><dt>结束时间</dt><dd>{formatTime(selectedJob.finished_at)}</dd></div>
                {displayedJobError?.error_message && (
                  <div className="is-wide">
                    <dt>失败原因</dt>
                    <dd>{displayedJobError.error_message}</dd>
                  </div>
                )}
              </dl>
              {selectedJob.video_url && (
                <VideoPreview
                  className="jianying-job-detail__preview"
                  src={selectedJob.video_url}
                  title={selectedJob.generated_draft_name || '剪映成片'}
                />
              )}
              <section className="jianying-job-attempts">
                <h4>设备执行记录</h4>
                <div
                  aria-label="设备执行记录列表"
                  className="jianying-job-attempts__scroll"
                  tabIndex={0}
                >
                  {attemptMessage ? (
                    <p className="jianying-notice" role="alert">{attemptMessage}</p>
                  ) : null}
                  {attemptsLoading ? (
                    <div className="jianying-job-attempts__empty"><Spinner size="sm" /> 正在读取</div>
                  ) : null}
                  {!attemptsLoading && attemptsByCycle.map((group, groupIndex) => (
                    <details
                      className="jianying-job-attempt-cycle"
                      key={group.cycle}
                      open={
                        groupIndex === 0
                        && shouldDefaultExpandJianyingAttemptCycle(group.items)
                      }
                    >
                      <summary>
                        <strong>第 {group.cycle} 轮</strong>
                        <span>{group.items.length} 次设备执行</span>
                      </summary>
                      <div className="jianying-job-attempt-cycle__items">
                        {group.items.map((attempt) => (
                          <article className="jianying-job-attempt" key={attempt.id}>
                            <div className="jianying-job-attempt__head">
                              <div>
                                <strong>设备：{attempt.device_name || attempt.device_id}</strong>
                                <span>{formatTime(attempt.assigned_at)}</span>
                              </div>
                              <span className={`jianying-status jianying-status--${getAttemptStatusTone(attempt.status)}`}>
                                {ATTEMPT_STATUS_LABELS[attempt.status] || attempt.status}
                              </span>
                            </div>
                            <dl>
                              <div><dt>执行阶段</dt><dd>{getJianyingAttemptStageLabel(attempt)}</dd></div>
                              <div><dt>结束时间</dt><dd>{formatTime(attempt.finished_at)}</dd></div>
                              {attempt.error_message ? (
                                <div className="is-wide"><dt>失败原因</dt><dd>{attempt.error_message}</dd></div>
                              ) : null}
                            </dl>
                          </article>
                        ))}
                      </div>
                    </details>
                  ))}
                  {!attemptsLoading && !attemptMessage && attempts.length === 0 ? (
                    <div className="jianying-job-attempts__empty">尚无设备执行记录</div>
                  ) : null}
                </div>
              </section>
            </>
          )}
        </aside>
      </div>

      <Modal
        show={Boolean(retryCandidate)}
        onHide={() => !busyId && setRetryCandidate(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>重试剪映任务</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            重试会开启新的调度周期，并从头生成当前设备需要的本地草稿。
          </p>
          {retryDeviceMessage ? (
            <p className="jianying-notice mb-3" role="alert">{retryDeviceMessage}</p>
          ) : null}
          <Form.Group controlId="jianying-retry-preferred-device">
            <Form.Label>优先设备（可选）</Form.Label>
            <Form.Select
              value={retryPreferredDeviceId}
              disabled={retryDevicesLoading || Boolean(busyId)}
              onChange={(event) => setRetryPreferredDeviceId(event.target.value)}
            >
              <option value="">自动选择</option>
              {retryEligibleDevices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name}（优先级 {device.priority}）
                </option>
              ))}
            </Form.Select>
            <Form.Text>
              这里只显示在线、未达积压上限且拥有该模板的设备；最终仍由后端重新校验。
            </Form.Text>
          </Form.Group>
          {retryDevicesLoading ? (
            <div className="mt-2 text-secondary"><Spinner size="sm" /> 正在读取可用设备</div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={Boolean(busyId)}
            onClick={() => setRetryCandidate(null)}
          >
            取消
          </Button>
          <Button
            variant="danger"
            disabled={Boolean(busyId) || retryDevicesLoading}
            onClick={retryJob}
          >
            {busyId ? <><Spinner size="sm" /> 重试中</> : '确认重试'}
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
};

export default JianyingJobsPage;
